import { useEffect, useRef, useState } from "react";

type CopyState = "idle" | "copied" | "failed";

/**
 * The save-your-key ceremony shown right after Start-now mints a code
 * (front-door redesign, 2026-07-04 — Rich, live staging review: the old
 * in-door "You're in" card swap was too casual for something this
 * load-bearing, and "Aurora doesn't use accounts" was never said out loud).
 *
 * Structurally mirrors RunState.tsx's PassphraseGate — code + Copy, a
 * checkbox that ARMS Continue, the button that commits — same two-beat
 * ceremony, rendered in Landing's own paper/serif/oxblood register instead
 * of the wizard's theme (this overlays Landing, not the wizard).
 *
 * Deliberately hard to lose: no backdrop-click dismiss, no Esc dismiss, and
 * focus is trapped inside while open. Losing this code by accident — not a
 * user closing the modal on purpose — is the failure mode this exists to
 * prevent. The two ways out are Continue (armed) and the quiet "Not now"
 * escape; the caller treats "Not now" as abandoning the mint (the unused
 * code is silently reaped by the existing 30-day self-serve TTL).
 */
export function SaveCodeModal({
  code,
  onContinue,
  onNotNow,
}: {
  code: string;
  onContinue: () => void;
  onNotNow: () => void;
}) {
  const [ackArmed, setAckArmed] = useState(false);
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const modalRef = useRef<HTMLDivElement | null>(null);
  const copyBtnRef = useRef<HTMLButtonElement | null>(null);

  // Focus trap: Tab/Shift-Tab cycles inside the modal so keyboard focus
  // can't wander to the (still-present, but inert-behind-the-overlay) door
  // underneath. Escape is swallowed rather than wired to close — this is
  // the one modal in the app where Esc must NOT be an exit.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        return;
      }
      if (e.key !== "Tab" || !modalRef.current) return;
      const focusables = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Land keyboard focus on Copy — the first thing worth doing here.
  useEffect(() => {
    copyBtnRef.current?.focus();
  }, []);

  function handleCopy() {
    navigator.clipboard.writeText(code).then(
      () => {
        setCopyState("copied");
        window.setTimeout(() => setCopyState("idle"), 2500);
      },
      () => {
        setCopyState("failed");
        window.setTimeout(() => setCopyState("idle"), 2500);
      },
    );
  }

  return (
    <div
      className="save-code-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-code-title"
      // NO backdrop-click dismiss, deliberately — see module comment.
    >
      <div className="save-code-modal" ref={modalRef}>
        <h2 id="save-code-title" className="save-code-title">
          You&rsquo;re in — save your code.
        </h2>
        <p className="save-code-body">
          Aurora doesn&rsquo;t use accounts — no email, no password, nothing
          to sign up for. This code is your only key: it&rsquo;s how you get
          back in if you close this tab, and how your payment stays attached
          to your analysis. Aurora won&rsquo;t show it again after you leave
          this page.
        </p>
        <div className="save-code-row">
          <code className="save-code-value">{code}</code>
          <button
            type="button"
            className="save-code-copy"
            ref={copyBtnRef}
            onClick={handleCopy}
          >
            {copyState === "copied"
              ? "Copied ✓"
              : copyState === "failed"
                ? "Select & copy"
                : "Copy"}
          </button>
        </div>
        <label className="save-code-ack">
          <input
            type="checkbox"
            checked={ackArmed}
            onChange={(e) => setAckArmed(e.target.checked)}
          />
          <span>I&rsquo;ve saved my code somewhere safe.</span>
        </label>
        <button
          type="button"
          className="save-code-continue"
          disabled={!ackArmed}
          onClick={onContinue}
        >
          Continue →
        </button>
        <button
          type="button"
          className="save-code-notnow"
          onClick={onNotNow}
        >
          Not now — back to the page.
        </button>
      </div>
    </div>
  );
}
